/**
 * PitchKit — "Build the pitch"
 *
 * Four internal phases:
 *   0. scope — "What type of creative?" (location vs brand) — skipped if scope already set
 *   1. configure — Full Creative Studio ConfigureStep (photos, reviews,
 *      characters, video format, campaign brief, logo overlay)
 *   2. generating — Backend-driven seller creative generation + polling
 *   3. review — Real backend creative preview + "Choose a plan" CTA
 */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Share2, Play, Check,
  RefreshCw, MapPin, Store, Building2, CircleCheck, Video, TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import { creativeSetFromCreative } from '@/services/sellerFlowMappers';
import { SellerFlowApiError } from '@/services/sellerFlowApi';
import { directorApiClient } from '@/services/directorApi';
import { videoApiClient } from '@/services/videoApi';
import { DEFAULT_SCRIPT_MODE, normalizeScriptMode } from '@/lib/videoConfig';
import useAuthenticatedMediaUrl from '@/hooks/useAuthenticatedMediaUrl';
import ConfigureStep from '@/components/creatives/ConfigureStep';
import CinematicLoader from '@/components/creatives/CinematicLoader';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';

// ── Scope options for creative type ──────────────────────────────────────────
const SCOPE_OPTIONS = [
  { id: 'single_location', label: 'This location', desc: 'Ad features this specific store', icon: MapPin },
  { id: 'multiple_locations', label: 'Multiple locations', desc: 'Ad covers a group of stores', icon: Store },
  { id: 'brand_wide', label: 'The whole brand', desc: 'Brand-level messaging, no specific address', icon: Building2 },
];

const SCOPE_DETAILS = {
  single_location: {
    fit: 'Best for one storefront or practice',
    summary: 'Keeps the pitch anchored to one address, one local message, and one clear call to action.',
    bullets: [
      'Highlights the specific location and nearby audience.',
      'Works best when the business wants local foot traffic or calls.',
      'Keeps the ad concrete and easiest for buyers to understand.',
    ],
  },
  multiple_locations: {
    fit: 'Best for grouped local businesses',
    summary: 'Lets the pitch cover a cluster of stores without forcing every message through one single address.',
    bullets: [
      'Uses a broader message across several nearby locations.',
      'Good when the buyer wants one offer promoted across multiple stores.',
      'Balances local relevance with a little more scale.',
    ],
  },
  brand_wide: {
    fit: 'Best for brand-led awareness',
    summary: 'Focuses the pitch on the overall brand rather than one storefront, neighborhood, or address.',
    bullets: [
      'Leans into broader brand messaging and recognition.',
      'Useful when the business has several locations or no single flagship.',
      'Works best when the offer is not tied to one immediate local destination.',
    ],
  },
};

// ── Generation simulation steps ──────────────────────────────────────────────
const GENERATION_STEPS = [
  { label: 'Preparing creative project' },
  { label: 'Applying your creative inputs' },
  { label: 'Rendering video' },
  { label: 'Finalizing preview' },
];

const DEFAULT_CAMPAIGN_BRIEF = {
  purpose: 'awareness',
  seasonal_theme: null,
  tone: 'auto',
  channel: 'ctv',
  offers: [],
  notes: '',
};

const EMPTY_CAMPAIGN_OPTIONS = {
  purpose: [],
  seasonal_theme: [],
  tone: [],
  channel: [],
};

const PITCH_BINDING_STORAGE_PREFIX = 'cornercast_pitchkit_binding';

function getPitchBindingStorageKey(businessId) {
  return businessId ? `${PITCH_BINDING_STORAGE_PREFIX}:${businessId}` : null;
}

function readRememberedPitchBinding(businessId) {
  const key = getPitchBindingStorageKey(businessId);
  if (!key) return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRememberedPitchBinding(businessId, bindingId) {
  const key = getPitchBindingStorageKey(businessId);
  if (!key || !bindingId) return;
  try {
    sessionStorage.setItem(key, bindingId);
  } catch {
    // ignore storage failures
  }
}

function clearRememberedPitchBinding(businessId) {
  const key = getPitchBindingStorageKey(businessId);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

function getRecoverableCreativePriority(creative = {}) {
  switch (creative.status) {
    case 'processing':
      return 0;
    case 'failed':
      return 1;
    case 'rejected':
      return 2;
    default:
      return 9;
  }
}

function getRecoverableCreativeTimestamp(creative = {}) {
  return new Date(creative.updatedAt || creative.createdAt || 0).getTime();
}

function pickRecoverableCreative(creatives = [], preferredBindingId = null) {
  if (!Array.isArray(creatives) || creatives.length === 0) return null;

  if (preferredBindingId) {
    return creatives.find(
      (creative) => creative.bindingId === preferredBindingId || creative.id === preferredBindingId,
    ) || null;
  }

  const candidates = creatives
    .filter((creative) => ['processing', 'failed', 'rejected'].includes(creative.status))
    .sort((a, b) => {
      const priorityDiff = getRecoverableCreativePriority(a) - getRecoverableCreativePriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      return getRecoverableCreativeTimestamp(b) - getRecoverableCreativeTimestamp(a);
    });

  return candidates[0] || null;
}

function buildSourcePayload(businessRecord, businessProfile) {
  const source = {
    place_id: businessProfile?.place_id || businessRecord?.origin?.place_id || null,
    maps_url: businessRecord?.origin?.maps_url || businessProfile?.maps_url || null,
    website_url: businessProfile?.website_url || businessRecord?.website_url || businessRecord?.origin?.website_url || null,
  };

  return Object.values(source).some(Boolean) ? source : null;
}

function normalizePreviewMediaUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${videoApiClient.baseUrl}${url}`;
  }
  return url;
}

function isBackendRecognizedPhotoId(photoId) {
  return typeof photoId === 'string'
    && photoId.length > 0
    && !photoId.startsWith('mock-')
    && !photoId.startsWith('upload-');
}

function isBackendCompatibleImagePayload(value) {
  return typeof value === 'string' && value.startsWith('data:image/');
}

function buildSelectedPhotosPayload(photos = []) {
  const normalized = photos
    .filter((photo) => photo.selected)
    .map((photo) => {
      const rawImagePayload = photo.data_url || photo.url || photo.photo_url || null;
      const photoId = isBackendRecognizedPhotoId(photo.photo_id) ? photo.photo_id : null;
      const dataUrl = isBackendCompatibleImagePayload(rawImagePayload) ? rawImagePayload : null;

      if (!photoId && !dataUrl) {
        return null;
      }

      return {
        photo_id: photoId,
        photo_name_id: photo.photo_name_id || null,
        data_url: dataUrl,
        caption: photo.caption || null,
        selected: true,
      };
    })
    .filter(Boolean);

  return normalized.length > 0 ? normalized : null;
}

function buildSelectedReviewsPayload(reviews = [], selectedReviews = []) {
  const selectedIds = new Set(selectedReviews);
  return reviews.map((review) => ({
    review_id: review.review_id || null,
    text: review.text || '',
    author: review.author || null,
    rating: review.rating || null,
    selected: selectedIds.has(review.review_id),
  }));
}

function buildCharactersPayload(characters = []) {
  return characters.map((character) => ({
    name: character.name || null,
    role: character.role || null,
    description: character.description || null,
    data_urls: Array.isArray(character.data_urls) && character.data_urls.length
      ? character.data_urls
      : character.image_url
        ? [character.image_url]
        : [],
  })).filter((character) => character.data_urls.length > 0);
}

function extractBusinessDataPayload(response) {
  return response?.result?.business
    || response?.result?.business_data
    || response?.business
    || response?.business_data
    || response
    || null;
}

function normalizePhoto(photo) {
  return {
    ...photo,
    selected: photo.selected !== undefined ? photo.selected : true,
  };
}

function normalizeReview(review, index) {
  return {
    review_id: review.review_id || `review-${index}`,
    text: typeof review === 'string' ? review : (review.text || review.content || ''),
    rating: typeof review === 'object' ? review.rating : null,
    author: typeof review === 'object' ? review.author : null,
    selected: review.selected !== undefined ? review.selected : true,
  };
}

function normalizeCampaignBriefPayload(brief) {
  if (!brief) return { ...DEFAULT_CAMPAIGN_BRIEF };
  const source = brief.brief || brief;
  return {
    ...DEFAULT_CAMPAIGN_BRIEF,
    ...source,
    offers: Array.isArray(source?.offers) ? source.offers : [],
    notes: source?.notes || '',
  };
}

function getGenerationPresentation(creative) {
  if (!creative) {
    return {
      stepIndex: 0,
      title: 'Creating your pitch...',
      description: 'Starting a real creative generation request for this workspace.',
    };
  }

  if (['failed', 'rejected'].includes(creative.status)) {
    return {
      stepIndex: 2,
      title: 'Creative generation failed',
      description: creative.errorDetail || 'The backend returned a generation failure for this creative.',
    };
  }

  switch (creative.rawState) {
    case 'queued':
    case 'pending':
      return {
        stepIndex: 1,
        title: 'Preparing your pitch...',
        description: 'Your creative has been created on the backend and is applying the business, media, and brief inputs now.',
      };
    case 'processing':
    case 'running':
    case 'rendering':
    case 'generating':
    case 'retrying':
    case 'restarting':
    case 'data_ready':
      return {
        stepIndex: 2,
        title: 'Rendering your pitch...',
        description: 'Building your ad now — this usually takes a moment.',
      };
    case 'completed':
      return {
        stepIndex: 3,
        title: 'Finalizing your pitch...',
        description: 'Wrapping up — saving this ad to your library.',
      };
    default:
      return {
        stepIndex: 0,
        title: 'Creating your pitch...',
        description: 'Starting a real creative generation request for this workspace.',
      };
  }
}

function getBusinessProfileEnrichmentMessage(status, errorDetail = null) {
  if (status === 'failed') {
    return errorDetail || 'The business profile build failed. Retry it before generating creative for this workspace.';
  }

  if (status === 'pending') {
    return 'We’re still building the business profile for this workspace. Creative generation will be available once enrichment finishes.';
  }

  return '';
}

function extractCanonicalBusinessData(profile) {
  const canonical = profile?.canonicalProfileJson;
  if (!canonical || typeof canonical !== 'object') return null;

  return {
    name: canonical.name || profile?.name || '',
    address: canonical.address || profile?.address || '',
    city: canonical.city || '',
    state: canonical.state || '',
    stars: canonical.stars ?? profile?.rating ?? 0,
    reviews: Array.isArray(canonical.reviews) ? canonical.reviews : [],
    photos: Array.isArray(canonical.photos) ? canonical.photos : [],
    logo_data_url: canonical.brand_identity?.logo_data_url || null,
    additional_info: canonical.additional_info || null,
  };
}

function normalizeRecoveredReview(review, index) {
  return normalizeReview(review, index);
}

function normalizeRecoveredCharacter(character, index) {
  return {
    character_id: character.character_id || character.id || `recovered-character-${index}`,
    name: character.name || '',
    role: character.role || '',
    description: character.description || '',
    data_urls: Array.isArray(character.data_urls) ? character.data_urls : [],
    image_url: Array.isArray(character.data_urls) ? character.data_urls[0] || null : null,
  };
}

export default function PitchKit() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deal = useDealFlow();

  // ── Scope gating ──────────────────────────────────────────────────────────
  const existingScope = deal.scope?.type
    || deal.businesses?.[deal.activeBusinessId]?.scope
    || null;
  const [pendingScope, setPendingScope] = useState('single_location');

  const handleScopeConfirm = () => {
    // Save scope to deal state + business record
    deal.setScope({ type: pendingScope, locations: [] });
    const bizId = deal.activeBusinessId || deal.businessProfile?.id;
    if (bizId && deal.businesses?.[bizId]) {
      deal.addBusiness({ ...deal.businesses[bizId], scope: pendingScope });
    }
    setPhase('configure');
  };

  // ── Phase management ─────────────────────────────────────────────────────
  // 'scope' | 'configure' | 'generating' | 'review'
  const [phase, setPhase] = useState(existingScope ? 'configure' : 'scope');

  // ── Video format ─────────────────────────────────────────────────────────
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(15);
  const [scriptMode, setScriptMode] = useState(DEFAULT_SCRIPT_MODE);
  const [silentVideo, setSilentVideo] = useState(false);

  // ── Campaign brief ───────────────────────────────────────────────────────
  const [campaignBrief, setCampaignBrief] = useState({ ...DEFAULT_CAMPAIGN_BRIEF });
  const [campaignOptions, setCampaignOptions] = useState(EMPTY_CAMPAIGN_OPTIONS);
  const [campaignOptionsError, setCampaignOptionsError] = useState(null);

  // ── Photos ───────────────────────────────────────────────────────────────
  const [photos, setPhotos] = useState([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // ── Reviews ──────────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState([]);
  const [selectedReviews, setSelectedReviews] = useState([]);

  // ── Characters ───────────────────────────────────────────────────────────
  const [characters, setCharacters] = useState([]);

  // ── Logo overlay ─────────────────────────────────────────────────────────
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoOverlayEnabled, setLogoOverlayEnabled] = useState(true);
  const [logoOverlayPosition, setLogoOverlayPosition] = useState('bottom-right');
  const [logoOverlayOpacity, setLogoOverlayOpacity] = useState(0.85);
  const [sourceProjectId, setSourceProjectId] = useState(null);
  const [sourceBusinessData, setSourceBusinessData] = useState(null);
  const [isLoadingSourceMedia, setIsLoadingSourceMedia] = useState(false);
  const [sourceMediaError, setSourceMediaError] = useState(null);
  const sourceBootstrapKeyRef = useRef(null);
  const recoveredPitchBindingRef = useRef(null);
  const recoveredGenerationRequestRef = useRef(false);

  const activeBusinessId = deal.activeBusinessId || deal.businessProfile?.id;
  const businessRecord = activeBusinessId ? deal.businesses?.[activeBusinessId] || null : null;
  const activeBusinessProfile = activeBusinessId
    ? deal.businessProfilesByBusiness?.[activeBusinessId]
      || (deal.activeBusinessId === activeBusinessId ? deal.businessProfile : null)
    : deal.businessProfile;
  const freshStartRequested = searchParams.get('fresh') === '1';
  const rememberedPitchBindingId = freshStartRequested ? null : readRememberedPitchBinding(activeBusinessId);
  const requestedPitchBindingId = freshStartRequested ? null : searchParams.get('binding_id');
  const preferredRecoveryBindingId = requestedPitchBindingId || rememberedPitchBindingId;
  const enrichmentStatus = activeBusinessProfile?.enrichmentStatus || (activeBusinessId ? 'pending' : null);
  const enrichmentErrorDetail = activeBusinessProfile?.enrichmentErrorDetail || null;
  const enrichmentBlocked = enrichmentStatus === 'pending' || enrichmentStatus === 'failed';
  const enrichmentMessage = getBusinessProfileEnrichmentMessage(
    enrichmentStatus,
    enrichmentErrorDetail,
  );
  const canonicalBusinessData = extractCanonicalBusinessData(activeBusinessProfile);
  const hasSeededApiMedia = Boolean(
    canonicalBusinessData?.photos?.length
    || canonicalBusinessData?.reviews?.length
    || canonicalBusinessData?.logo_data_url,
  );

  // ── Backend creative state ──────────────────────────────────────────────
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  const [backendCreative, setBackendCreative] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [isSubmittingGeneration, setIsSubmittingGeneration] = useState(false);
  const [isPollingCreative, setIsPollingCreative] = useState(false);
  const [autoRecoverySuppressed, setAutoRecoverySuppressed] = useState(false);
  const [isRecoveringPitch, setIsRecoveringPitch] = useState(false);
  const [recoveryResolved, setRecoveryResolved] = useState(false);
  const pollTimerRef = useRef(null);

  // ── Set deal stage on mount ──────────────────────────────────────────────
  useEffect(() => {
    deal.setCurrentStage('pitch');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up timers on unmount
  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const sourcePayload = buildSourcePayload(businessRecord, activeBusinessProfile);

  useEffect(() => {
    if (!activeBusinessId) return;
    deal.refreshBusinessProfileForBusiness(activeBusinessId, { setActive: true }).catch((error) => {
      console.warn('Could not refresh business-profile state for PitchKit', error);
    });
  }, [activeBusinessId, deal.refreshBusinessProfileForBusiness]);

  useEffect(() => {
    recoveredPitchBindingRef.current = null;
    sourceBootstrapKeyRef.current = null;
    recoveredGenerationRequestRef.current = false;
    setAutoRecoverySuppressed(false);
    setIsRecoveringPitch(false);
    setRecoveryResolved(false);
  }, [activeBusinessId]);

  useEffect(() => {
    if (!canonicalBusinessData) return;

    setSourceBusinessData((prev) => prev || canonicalBusinessData);
    setPhotos((prev) => (prev.length ? prev : canonicalBusinessData.photos.map(normalizePhoto)));

    const normalizedReviews = canonicalBusinessData.reviews.map(normalizeReview);
    setReviews((prev) => (prev.length ? prev : normalizedReviews));
    setSelectedReviews((prev) => (
      prev.length
        ? prev
        : normalizedReviews.filter((review) => review.selected).map((review) => review.review_id)
    ));
    setLogoDataUrl((prev) => prev || canonicalBusinessData.logo_data_url || null);
  }, [canonicalBusinessData]);

  useEffect(() => {
    if (!activeBusinessId || enrichmentStatus !== 'pending') return undefined;

    let cancelled = false;
    let timerId = null;

    const poll = async () => {
      try {
        await deal.refreshBusinessProfileForBusiness(activeBusinessId, { setActive: true });
      } catch (error) {
        if (!cancelled) {
          console.warn('Could not refresh pending business-profile state in PitchKit', error);
        }
      } finally {
        if (!cancelled) {
          timerId = window.setTimeout(poll, 5000);
        }
      }
    };

    timerId = window.setTimeout(poll, 5000);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [activeBusinessId, deal.refreshBusinessProfileForBusiness, enrichmentStatus]);

  useEffect(() => {
    if (!activeBusinessId || enrichmentStatus !== 'ready') {
      return undefined;
    }

    directorApiClient.getCampaignOptions().then((optionsResponse) => {
      setCampaignOptions(optionsResponse || EMPTY_CAMPAIGN_OPTIONS);
      setCampaignOptionsError(optionsResponse ? null : 'Could not load campaign options.');
    }).catch((error) => {
      console.warn('Could not load campaign options for PitchKit', error);
      setCampaignOptions(EMPTY_CAMPAIGN_OPTIONS);
      setCampaignOptionsError('Could not load campaign options.');
    });
  }, [activeBusinessId, enrichmentStatus]);

  // ── Map deal.businessProfile → realBusinessData (ConfigureStep format) ──
  const realBusinessData = {
    name: sourceBusinessData?.name || activeBusinessProfile?.name || businessRecord?.name || '',
    address: sourceBusinessData?.address || activeBusinessProfile?.address || businessRecord?.address || '',
    phone: '',
    website: activeBusinessProfile?.website_url || businessRecord?.website_url || '',
    category: activeBusinessProfile?.category || businessRecord?.category || '',
    placeId: activeBusinessProfile?.place_id || '',
    rating: sourceBusinessData?.stars || activeBusinessProfile?.rating || businessRecord?.rating || 0,
    reviews_count: reviews.length || activeBusinessProfile?.reviews || businessRecord?.reviews || 0,
    logo_data_url: logoDataUrl || sourceBusinessData?.logo_data_url || null,
  };
  const previewCreative = backendCreative || (activeBusinessId
    ? (deal.creatives?.[activeBusinessId] || []).find((creative) => creative.id === deal.selectedCreativeId)
    : null);
  const previewAsset = previewCreative?.assets?.[0] || deal.creativeSet?.assets?.[0] || null;
  const previewVideoUrl = normalizePreviewMediaUrl(
    previewAsset?.url || previewCreative?.videoUrl || previewCreative?.previewUrl || '',
  );
  const previewPosterUrl = normalizePreviewMediaUrl(
    previewCreative?.thumbnailUrl || previewAsset?.thumbnail_url || '',
  );
  const {
    resolvedUrl: playablePreviewVideoUrl,
    isLoading: isLoadingPreviewVideo,
  } = useAuthenticatedMediaUrl(previewVideoUrl);
  const [previewPosterFailed, setPreviewPosterFailed] = useState(false);
  const purposeLabel = campaignBrief.purpose === 'awareness' ? 'Brand awareness'
    : campaignBrief.purpose === 'seasonal' ? 'Seasonal campaign'
      : campaignBrief.purpose === 'promotion' ? 'Promotion / offer'
        : campaignBrief.purpose || 'Awareness';
  const toneLabel = campaignBrief.tone === 'auto' ? 'Auto' : campaignBrief.tone || 'Auto';
  const formatLabel = aspectRatio === '16:9' ? 'CTV' : aspectRatio === '9:16' ? 'Vertical social' : 'Square social';
  const scopeLabel = SCOPE_OPTIONS.find((option) => option.id === (existingScope || pendingScope))?.label || 'This location';
  const businessName = activeBusinessProfile?.name || businessRecord?.name || 'Business';

  useEffect(() => {
    setPreviewPosterFailed(false);
  }, [previewPosterUrl]);

  const setPitchBindingQuery = useCallback((bindingId = null) => {
    const nextParams = new URLSearchParams(searchParams);
    const currentBindingId = nextParams.get('binding_id');

    if ((bindingId || null) === (currentBindingId || null)) {
      return;
    }

    if (bindingId) {
      nextParams.set('binding_id', bindingId);
      nextParams.delete('fresh');
    } else {
      nextParams.delete('binding_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const rememberPitchBinding = useCallback((bindingId) => {
    writeRememberedPitchBinding(activeBusinessId, bindingId);
    setPitchBindingQuery(bindingId);
  }, [activeBusinessId, setPitchBindingQuery]);

  const clearPitchBinding = useCallback(() => {
    clearRememberedPitchBinding(activeBusinessId);
    setPitchBindingQuery(null);
  }, [activeBusinessId, setPitchBindingQuery]);

  const markPitchStartFresh = useCallback(() => {
    clearRememberedPitchBinding(activeBusinessId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('binding_id');
    nextParams.set('fresh', '1');
    setSearchParams(nextParams, { replace: true });
  }, [activeBusinessId, searchParams, setSearchParams]);

  const resetRecoveredGenerationRequest = useCallback(() => {
    recoveredGenerationRequestRef.current = false;
  }, []);

  const applyVideoConfigToForm = useCallback((videoConfig = {}) => {
    if (!videoConfig || typeof videoConfig !== 'object') return;

    if (videoConfig.aspectRatio || videoConfig.aspect_ratio) {
      setAspectRatio(videoConfig.aspectRatio || videoConfig.aspect_ratio);
    }

    const nextDuration = Number(videoConfig.length ?? videoConfig.duration);
    if (Number.isFinite(nextDuration) && nextDuration > 0) {
      setDuration(nextDuration);
    }

    const nextScriptMode = videoConfig.scriptMode || videoConfig.script_mode;
    if (nextScriptMode) {
      setScriptMode(normalizeScriptMode(nextScriptMode));
    }

    if (typeof (videoConfig.logoOverlayEnabled ?? videoConfig.logo_overlay_enabled) === 'boolean') {
      setLogoOverlayEnabled(Boolean(videoConfig.logoOverlayEnabled ?? videoConfig.logo_overlay_enabled));
    }

    if (videoConfig.logoOverlayPosition || videoConfig.logo_overlay_position) {
      setLogoOverlayPosition(videoConfig.logoOverlayPosition || videoConfig.logo_overlay_position);
    }

    const nextOverlayOpacity = videoConfig.logoOverlayOpacity ?? videoConfig.logo_overlay_opacity;
    if (typeof nextOverlayOpacity === 'number') {
      setLogoOverlayOpacity(nextOverlayOpacity);
    }
  }, []);

  const hydrateSourceProjectContext = useCallback(async (
    jobId,
    {
      seedBusinessData = null,
      seedVideoConfig = null,
    } = {},
  ) => {
    if (!jobId) return;

    setSourceProjectId(jobId);
    setIsLoadingSourceMedia(true);
    setSourceMediaError(null);

    if (seedBusinessData) {
      setSourceBusinessData(seedBusinessData);
      if (!recoveredGenerationRequestRef.current) {
        setPhotos((seedBusinessData?.photos || []).map(normalizePhoto));
        const nextReviews = (seedBusinessData?.reviews || []).map(normalizeReview);
        setReviews(nextReviews);
        setSelectedReviews(
          nextReviews.filter((review) => review.selected).map((review) => review.review_id),
        );
        setLogoDataUrl(seedBusinessData?.logo_data_url || null);
      }
    }

    if (seedVideoConfig && !recoveredGenerationRequestRef.current) {
      applyVideoConfigToForm(seedVideoConfig);
    }

    try {
      const [businessResult, briefResult, charResult, videoConfigResult] = await Promise.allSettled([
        directorApiClient.getBusinessData(jobId),
        directorApiClient.getCampaignBrief(jobId),
        directorApiClient.getCharacters(jobId),
        directorApiClient.getVideoConfig(jobId),
      ]);

      if (businessResult.status === 'fulfilled') {
        const businessData = extractBusinessDataPayload(businessResult.value);
        setSourceBusinessData(businessData);
        if (!recoveredGenerationRequestRef.current) {
          setPhotos((businessData?.photos || []).map(normalizePhoto));
          const nextReviews = (businessData?.reviews || []).map(normalizeReview);
          setReviews(nextReviews);
          setSelectedReviews(
            nextReviews.filter((review) => review.selected).map((review) => review.review_id),
          );
          setLogoDataUrl(businessData?.logo_data_url || null);
        }
      } else if (!seedBusinessData) {
        console.warn('Could not load backend business data for PitchKit', businessResult.reason);
        if (!recoveredGenerationRequestRef.current) {
          setSourceBusinessData(null);
          setPhotos([]);
          setReviews([]);
          setSelectedReviews([]);
          setLogoDataUrl(null);
        }
      }

      if (briefResult.status === 'fulfilled') {
        if (!recoveredGenerationRequestRef.current) {
          setCampaignBrief(normalizeCampaignBriefPayload(briefResult.value));
        }
      } else {
        console.warn('Could not load backend campaign brief for PitchKit', briefResult.reason);
        if (!recoveredGenerationRequestRef.current) {
          setCampaignBrief({ ...DEFAULT_CAMPAIGN_BRIEF });
        }
      }

      if (charResult.status === 'fulfilled') {
        if (!recoveredGenerationRequestRef.current) {
          setCharacters(charResult.value?.characters || []);
        }
      } else {
        console.warn('Could not load backend characters for PitchKit', charResult.reason);
        if (!recoveredGenerationRequestRef.current) {
          setCharacters([]);
        }
      }

      if (videoConfigResult.status === 'fulfilled') {
        if (!recoveredGenerationRequestRef.current) {
          applyVideoConfigToForm(videoConfigResult.value);
        }
      } else if (!seedVideoConfig) {
        console.warn('Could not load backend video config for PitchKit', videoConfigResult.reason);
      }
    } catch (error) {
      console.error('Failed to hydrate PitchKit source project', error);
      setSourceMediaError(error?.message || 'Could not load business media for this pitch.');
    } finally {
      setIsLoadingSourceMedia(false);
    }
  }, [applyVideoConfigToForm]);

  const applyGenerationRequestToForm = useCallback((generationRequest = {}) => {
    if (!generationRequest || typeof generationRequest !== 'object') return;

    recoveredGenerationRequestRef.current = true;

    if (generationRequest.scope) {
      setPendingScope(generationRequest.scope);
    }
    if (generationRequest.aspect_ratio) {
      setAspectRatio(generationRequest.aspect_ratio);
    }
    if (generationRequest.duration) {
      setDuration(Number(generationRequest.duration));
    }
    setSilentVideo(Boolean(generationRequest.silent_video));

    const nextCampaignBrief = normalizeCampaignBriefPayload(generationRequest.campaign_brief_json);
    setCampaignBrief(nextCampaignBrief);

    const nextPhotos = Array.isArray(generationRequest.selected_photos_json)
      ? generationRequest.selected_photos_json.map((photo) => normalizePhoto({
        ...photo,
        selected: photo.selected ?? true,
        url: photo.data_url || photo.url || photo.photo_url || null,
      }))
      : [];
    if (nextPhotos.length) {
      setPhotos(nextPhotos);
    }

    const nextReviews = Array.isArray(generationRequest.selected_reviews_json)
      ? generationRequest.selected_reviews_json.map(normalizeRecoveredReview)
      : [];
    if (nextReviews.length) {
      setReviews(nextReviews);
      setSelectedReviews(
        nextReviews.filter((review) => review.selected).map((review) => review.review_id),
      );
    }

    const nextCharacters = Array.isArray(generationRequest.characters_json)
      ? generationRequest.characters_json.map(normalizeRecoveredCharacter)
      : [];
    if (nextCharacters.length) {
      setCharacters(nextCharacters);
    }

    const logoOverlay = generationRequest.logo_overlay_json || {};
    if (logoOverlay.logo_data_url) {
      setLogoDataUrl(logoOverlay.logo_data_url);
    }
    if (typeof logoOverlay.enabled === 'boolean') {
      setLogoOverlayEnabled(logoOverlay.enabled);
    }
    if (logoOverlay.position) {
      setLogoOverlayPosition(logoOverlay.position);
    }
    if (typeof logoOverlay.opacity === 'number') {
      setLogoOverlayOpacity(logoOverlay.opacity);
    }

    const metadata = generationRequest.metadata_json || {};
    setScriptMode(normalizeScriptMode(metadata.script_mode));
  }, []);

  const syncCreativePreview = useCallback((creative) => {
    if (creative?.bindingId) {
      rememberPitchBinding(creative.bindingId);
    }
    setBackendCreative(creative);
    deal.setCreativeSet(
      creative
        ? creativeSetFromCreative(creative, businessName)
        : null,
    );
  }, [businessName, deal.setCreativeSet, rememberPitchBinding]);

  const applyRecoveredCreativeState = useCallback((creative) => {
    if (!creative) return;

    if (creative.generationRequestJson) {
      applyGenerationRequestToForm(creative.generationRequestJson);
    }
    syncCreativePreview(creative);
    setSavedToLibrary(creative.status !== 'draft');

    if (creative.status === 'ready') {
      setGenerateError(null);
      setIsSubmittingGeneration(false);
      setIsPollingCreative(false);
      setPhase('review');
      return;
    }

    if (['failed', 'rejected'].includes(creative.status)) {
      setGenerateError(creative.errorDetail || 'Creative generation failed.');
      setIsSubmittingGeneration(false);
      setIsPollingCreative(false);
      setPhase('generating');
      return;
    }

    if (creative.status === 'draft') {
      setGenerateError(null);
      setIsSubmittingGeneration(false);
      setIsPollingCreative(false);
      setSavedToLibrary(false);
      setPhase('configure');
      if (creative.jobId) {
        hydrateSourceProjectContext(creative.jobId).catch((error) => {
          console.warn('Could not hydrate recovered PitchKit draft', error);
        });
      }
      return;
    }

    setGenerateError(null);
    setIsSubmittingGeneration(false);
    setIsPollingCreative(true);
    setPhase('generating');
  }, [applyGenerationRequestToForm, hydrateSourceProjectContext, syncCreativePreview]);

  useEffect(() => {
    if (!activeBusinessId) return undefined;

    let cancelled = false;

    const recoverPitchState = async () => {
      if (freshStartRequested) {
        clearRememberedPitchBinding(activeBusinessId);
        if (!cancelled) {
          setIsRecoveringPitch(false);
          setRecoveryResolved(true);
        }
        return;
      }

      if (autoRecoverySuppressed) {
        if (!cancelled) {
          setIsRecoveringPitch(false);
          setRecoveryResolved(true);
        }
        return;
      }

      if (
        preferredRecoveryBindingId
        && recoveredPitchBindingRef.current === preferredRecoveryBindingId
        && backendCreative?.bindingId === preferredRecoveryBindingId
      ) {
        if (!cancelled) {
          setIsRecoveringPitch(false);
          setRecoveryResolved(true);
        }
        return;
      }

      if (!cancelled) {
        setIsRecoveringPitch(true);
        setRecoveryResolved(false);
      }

      try {
        const candidateBindingIds = [];
        if (requestedPitchBindingId) {
          candidateBindingIds.push(requestedPitchBindingId);
        }
        if (rememberedPitchBindingId && rememberedPitchBindingId !== requestedPitchBindingId) {
          candidateBindingIds.push(rememberedPitchBindingId);
        }

        let loadedCreatives = deal.creatives?.[activeBusinessId] || [];
        const ensureCreativesLoaded = async () => {
          if (loadedCreatives.length) return loadedCreatives;
          loadedCreatives = await deal.loadCreativesForBusiness(activeBusinessId);
          return loadedCreatives || [];
        };

        let candidate = null;

        for (const bindingId of candidateBindingIds) {
          candidate = pickRecoverableCreative(loadedCreatives, bindingId);

          if (!candidate) {
            const creatives = await ensureCreativesLoaded();
            if (cancelled) return;
            candidate = pickRecoverableCreative(creatives, bindingId);
          }

          if (!candidate) {
            try {
              candidate = await deal.getWorkspaceCreativeForBusiness(activeBusinessId, bindingId);
            } catch (error) {
              console.warn('Could not recover pitch creative by binding', bindingId, error);
            }
            if (cancelled) return;
          }

          if (candidate) {
            break;
          }
        }

        if (!candidate) {
          const creatives = await ensureCreativesLoaded();
          if (cancelled) return;
          candidate = pickRecoverableCreative(creatives || []);
        }

        if (!candidate) {
          if (requestedPitchBindingId || rememberedPitchBindingId) {
            clearPitchBinding();
          }
          return;
        }

        recoveredPitchBindingRef.current = candidate.bindingId || candidate.id || null;
        applyRecoveredCreativeState(candidate);
      } catch (error) {
        if (cancelled) return;
        console.warn('Could not recover pitch creative state', error);
      } finally {
        if (!cancelled) {
          setIsRecoveringPitch(false);
          setRecoveryResolved(true);
        }
      }
    };

    recoverPitchState();

    return () => {
      cancelled = true;
    };
  }, [
    activeBusinessId,
    applyRecoveredCreativeState,
    autoRecoverySuppressed,
    backendCreative?.bindingId,
    clearPitchBinding,
    deal.creatives,
    deal.getWorkspaceCreativeForBusiness,
    deal.loadCreativesForBusiness,
    freshStartRequested,
    preferredRecoveryBindingId,
    rememberedPitchBindingId,
    requestedPitchBindingId,
  ]);

  useEffect(() => {
    const bootstrapKey = JSON.stringify({
      businessId: activeBusinessId || null,
      workspaceId: businessRecord?.workspaceId || activeBusinessProfile?.workspaceId || null,
      forceNew: freshStartRequested,
    });

    if (
      !activeBusinessId
      || enrichmentStatus !== 'ready'
      || !recoveryResolved
      || isRecoveringPitch
      || phase !== 'configure'
      || backendCreative
      || sourceBootstrapKeyRef.current === bootstrapKey
    ) {
      return undefined;
    }

    sourceBootstrapKeyRef.current = bootstrapKey;
    let cancelled = false;

    const loadSourceMedia = async () => {
      setIsLoadingSourceMedia(true);
      setSourceMediaError(null);

      try {
        const bootstrap = await deal.bootstrapPitchKitForBusiness(activeBusinessId, {
          force_new: freshStartRequested,
        });

        if (cancelled) return;

        const bootstrappedCreative = bootstrap?.creative || null;
        const jobId = bootstrap?.jobId || bootstrappedCreative?.jobId || null;

        if (!jobId || !bootstrappedCreative?.bindingId) {
          throw new Error('Could not bootstrap a seller-flow PitchKit draft for this workspace.');
        }

        recoveredPitchBindingRef.current = bootstrappedCreative.bindingId;
        syncCreativePreview(bootstrappedCreative);

        if (bootstrappedCreative.status !== 'draft') {
          applyRecoveredCreativeState(bootstrappedCreative);
          return;
        }

        setGenerateError(null);
        setSavedToLibrary(false);
        setPhase('configure');
        await hydrateSourceProjectContext(jobId, {
          seedBusinessData: bootstrap.business,
          seedVideoConfig: bootstrap.videoConfig,
        });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof SellerFlowApiError && error.isBusinessProfileEnrichmentIncomplete) {
          const currentStatus = error.currentBusinessProfileStatus || 'pending';
          const friendlyMessage = getBusinessProfileEnrichmentMessage(currentStatus, enrichmentErrorDetail);
          setGenerateError(friendlyMessage);
          setSourceMediaError(null);
        } else {
          console.error('Failed to load backend source media for PitchKit', error);
          setPhotos([]);
          setReviews([]);
          setSelectedReviews([]);
          setCharacters([]);
          setSourceProjectId(null);
          setSourceBusinessData(null);
          setSourceMediaError(error?.message || 'Could not load business media for this pitch.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSourceMedia(false);
        }
      }
    };

    loadSourceMedia();

    return () => {
      cancelled = true;
    };
  }, [
    activeBusinessId,
    activeBusinessProfile?.workspaceId,
    applyRecoveredCreativeState,
    backendCreative,
    businessRecord?.workspaceId,
    deal.bootstrapPitchKitForBusiness,
    enrichmentErrorDetail,
    enrichmentStatus,
    freshStartRequested,
    hydrateSourceProjectContext,
    isRecoveringPitch,
    phase,
    recoveryResolved,
    syncCreativePreview,
  ]);

  // ── Photo handlers ──────────────────────────────────────────────────────

  const handlePhotoToggle = (photoId, index) => {
    setPhotos((prev) =>
      prev.map((photo, i) => {
        if (photoId && photo.photo_id === photoId) return { ...photo, selected: !photo.selected };
        if (index !== undefined && i === index) return { ...photo, selected: !photo.selected };
        return photo;
      }),
    );
  };

  const handlePhotoUpload = async (file) => {
    if (!sourceProjectId) {
      toast.error('Business media is still loading. Try again in a moment.');
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const dataUrl = await directorApiClient.fileToDataUrl(file);
      const response = await directorApiClient.uploadPhoto(sourceProjectId, dataUrl, file.name);
      const newPhoto = {
        photo_id: response.photo_id,
        photo_name_id: response.photo_name_id || file.name,
        data_url: dataUrl,
        caption: file.name,
        selected: true,
      };
      setPhotos((prev) => [...prev, newPhoto]);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!sourceProjectId) return;

    try {
      await directorApiClient.deletePhoto(sourceProjectId, photoId);
      setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      console.error('Failed to delete photo', error);
      toast.error('Failed to delete photo');
    }
  };

  // ── Review handler ──────────────────────────────────────────────────────

  const handleReviewToggle = (reviewId) => {
    setSelectedReviews((prev) =>
      prev.includes(reviewId) ? prev.filter((id) => id !== reviewId) : [...prev, reviewId],
    );
  };

  // ── Character handlers ──────────────────────────────────────────────────

  const handleCharacterUpload = async (dataUrls, metadata = {}) => {
    if (!sourceProjectId) {
      toast.error('Business media is still loading. Try again in a moment.');
      return;
    }

    if (characters.length >= 3) {
      toast.error('Maximum 3 characters allowed');
      return;
    }

    try {
      const response = await directorApiClient.uploadCharacter(sourceProjectId, {
        data_urls: dataUrls,
        ...metadata,
      });
      setCharacters((prev) => [...prev, response]);
      toast.success('Character added');
    } catch (error) {
      console.error('Failed to upload character', error);
      toast.error('Failed to upload character');
    }
  };

  const handleCharacterUpdate = async (characterId, updates) => {
    if (!sourceProjectId) return;

    try {
      const response = await directorApiClient.updateCharacter(sourceProjectId, characterId, updates);
      setCharacters((prev) =>
        prev.map((character) => (character.character_id === characterId ? { ...character, ...response } : character)),
      );
      toast.success('Character updated');
    } catch (error) {
      console.error('Failed to update character', error);
      toast.error('Failed to update character');
    }
  };

  const handleCharacterDelete = async (characterId) => {
    if (!sourceProjectId) return;

    try {
      await directorApiClient.deleteCharacter(sourceProjectId, characterId);
      setCharacters((prev) => prev.filter((character) => character.character_id !== characterId));
      toast.success('Character removed');
    } catch (error) {
      console.error('Failed to delete character', error);
      toast.error('Failed to delete character');
    }
  };

  // ── Logo handlers ───────────────────────────────────────────────────────

  const handleLogoUpload = async (file) => {
    if (!sourceProjectId) {
      toast.error('Business media is still loading. Try again in a moment.');
      return;
    }

    try {
      const dataUrl = await directorApiClient.fileToDataUrl(file);
      const response = await directorApiClient.uploadLogo(sourceProjectId, dataUrl);
      setLogoDataUrl(response.logo_data_url || dataUrl);
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Failed to upload logo', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleLogoDelete = async () => {
    if (!sourceProjectId) return;

    try {
      await directorApiClient.deleteLogo(sourceProjectId);
      setLogoDataUrl(null);
      toast.success('Logo removed');
    } catch (error) {
      console.error('Failed to delete logo', error);
      toast.error('Failed to remove logo');
    }
  };

  const finalizeGeneratedCreative = useCallback(async (creative) => {
    if (!activeBusinessId || !creative?.bindingId) return;

    try {
      const selectedBindingId = await deal.selectCreativeForBusiness(activeBusinessId, creative);
      const selectedCreative = await deal.getWorkspaceCreativeForBusiness(activeBusinessId, selectedBindingId);
      const finalCreative = selectedCreative || { ...creative, isSelected: true, selectedOnWorkspace: true };

      syncCreativePreview(finalCreative);
      try {
        await deal.loadCreativesForBusiness(activeBusinessId);
      } catch (error) {
        console.warn('Creative library refresh failed after generation', error);
      }
      setGenerateError(null);
    } catch (error) {
      console.error('Creative was generated but could not be selected', error);
      syncCreativePreview(creative);
      setGenerateError(error?.message || 'Creative was generated, but the workspace selection could not be updated yet.');
    }

    setSavedToLibrary(true);
    setPhase('review');
  }, [
    activeBusinessId,
    deal.getWorkspaceCreativeForBusiness,
    deal.loadCreativesForBusiness,
    deal.selectCreativeForBusiness,
    syncCreativePreview,
  ]);

  const handleRetryBusinessProfileEnrichment = useCallback(async () => {
    if (!activeBusinessId) return;

    try {
      setGenerateError(null);
      await deal.enrichBusinessProfileForBusiness(activeBusinessId, null, { setActive: true });
      toast.success('Business profile build restarted');
    } catch (error) {
      console.error('Failed to retry business profile enrichment', error);
      toast.error(error?.message || 'Could not retry the business profile build');
    }
  }, [activeBusinessId, deal.enrichBusinessProfileForBusiness]);

  const handleResumeFailedCreative = useCallback(async () => {
    const workspaceId = businessRecord?.workspaceId || activeBusinessProfile?.workspaceId || null;

    if (!activeBusinessId || !workspaceId || !backendCreative?.bindingId) {
      toast.error('This failed creative cannot be resumed yet.');
      return;
    }

    try {
      setGenerateError(null);
      setIsSubmittingGeneration(true);
      setIsPollingCreative(false);

      const retriedCreative = await deal.retryWorkspaceCreativeForBusiness(
        activeBusinessId,
        backendCreative.bindingId,
      );

      applyRecoveredCreativeState(retriedCreative || {
        ...backendCreative,
        status: 'processing',
        rawState: 'queued',
        errorDetail: null,
      });
      toast.success('Creative generation resumed');
    } catch (error) {
      console.error('Failed to resume creative generation', error);
      setGenerateError(error?.message || 'Could not resume this creative generation yet.');
      setIsPollingCreative(false);
    } finally {
      setIsSubmittingGeneration(false);
    }
  }, [
    activeBusinessId,
    activeBusinessProfile?.workspaceId,
    applyRecoveredCreativeState,
    backendCreative,
    businessRecord?.workspaceId,
    deal.retryWorkspaceCreativeForBusiness,
  ]);

  // ── Generate (configure → generating → review) ─────────────────────────

  const handleGenerate = async () => {
    if (!activeBusinessId) {
      toast.error('A business workspace is required before generating a creative.');
      return;
    }

    if (enrichmentBlocked) {
      setGenerateError(enrichmentMessage);
      toast.error(enrichmentMessage);
      try {
        await deal.refreshBusinessProfileForBusiness(activeBusinessId, { setActive: true });
      } catch (error) {
        console.warn('Could not refresh business-profile state before generation', error);
      }
      return;
    }

    setGenerateError(null);
    setSavedToLibrary(false);
    setBackendCreative(null);
    setIsSubmittingGeneration(true);
    setIsPollingCreative(false);
    setPhase('generating');

    const source = buildSourcePayload(businessRecord, activeBusinessProfile);
    const payload = {
      source: source || undefined,
      scope: existingScope || pendingScope || null,
      aspect_ratio: aspectRatio,
      duration,
      silent_video: silentVideo,
      campaign_brief_json: {
        purpose: campaignBrief.purpose || null,
        objective: campaignBrief.purpose || null,
        seasonal_theme: campaignBrief.seasonal_theme || null,
        tone: campaignBrief.tone || null,
        channel: campaignBrief.channel || null,
        offers: campaignBrief.offers || [],
        notes: campaignBrief.notes || '',
      },
      selected_photos_json: buildSelectedPhotosPayload(photos) || undefined,
      selected_reviews_json: buildSelectedReviewsPayload(reviews, selectedReviews),
      characters_json: buildCharactersPayload(characters),
      logo_overlay_json: {
        enabled: logoOverlayEnabled,
        position: logoOverlayPosition,
        opacity: logoOverlayOpacity,
        logo_data_url: logoDataUrl || null,
      },
      metadata_json: {
        format: aspectRatio === '9:16' ? 'social_9_16' : aspectRatio === '1:1' ? 'social_1_1' : 'ctv_16_9',
        scope: existingScope || pendingScope || null,
        script_mode: scriptMode,
        silent_video: silentVideo,
        ui_surface: 'pitch_kit',
      },
    };

    try {
      const creative = await deal.generateWorkspaceCreativeForBusiness(activeBusinessId, payload);
      syncCreativePreview(creative);
      setSavedToLibrary(true);

      if (creative.status === 'ready') {
        await finalizeGeneratedCreative(creative);
        return;
      }

      if (['failed', 'rejected'].includes(creative.status)) {
        setGenerateError(creative.errorDetail || 'Creative generation failed.');
        return;
      }

      setIsPollingCreative(true);
    } catch (error) {
      console.error('Failed to generate workspace creative', error);
      if (error instanceof SellerFlowApiError && error.isBusinessProfileEnrichmentIncomplete) {
        const currentStatus = error.currentBusinessProfileStatus || 'pending';
        const friendlyMessage = getBusinessProfileEnrichmentMessage(currentStatus, enrichmentErrorDetail);
        setGenerateError(friendlyMessage);
        setPhase('configure');
        toast.error(friendlyMessage);
      } else {
        setGenerateError(error?.message || 'Could not start creative generation.');
        setPhase('configure');
        toast.error(error?.message || 'Could not start creative generation');
      }
    } finally {
      setIsSubmittingGeneration(false);
    }
  };

  // ── Share ───────────────────────────────────────────────────────────────

  const handleShare = () => {
    const shareUrl = previewCreative?.shareUrl || previewCreative?.previewUrl || previewCreative?.videoUrl || deal.creativeSet?.share_url;
    if (shareUrl) {
      navigator.clipboard?.writeText(shareUrl);
      toast.success('Preview link copied to clipboard');
    }
  };

  // ── Generate alternative (reset to configure) ────────────────────────

  const handleGenerateAlternative = () => {
    setAutoRecoverySuppressed(true);
    markPitchStartFresh();
    resetRecoveredGenerationRequest();
    setGenerateError(null);
    setBackendCreative(null);
    setIsPollingCreative(false);
    setSavedToLibrary(false);
    deal.setCreativeSet(null);
    setPhase('configure');
  };

  const handleEditSettings = () => {
    setAutoRecoverySuppressed(true);
    markPitchStartFresh();
    resetRecoveredGenerationRequest();
    setGenerateError(null);
    setBackendCreative(null);
    setIsPollingCreative(false);
    setSavedToLibrary(false);
    setPhase('configure');
  };

  // ── Back to Business Workspace ────────────────────────────────────────

  const handleBackToBusiness = () => {
    const bizId = activeBusinessId;
    if (bizId) {
      navigate(`/org/${orgSlug}/deal/business/${bizId}`);
    } else {
      navigate(`/org/${orgSlug}/leads`);
    }
  };

  const handleBuildOffer = () => {
    navigate(`/org/${orgSlug}/deal/${dealId}/offer`);
  };

  useEffect(() => {
    if (phase !== 'generating' || !isPollingCreative || !backendCreative?.bindingId || !activeBusinessId) return undefined;

    let cancelled = false;

    const pollCreative = async () => {
      try {
        const nextCreative = await deal.getWorkspaceCreativeForBusiness(activeBusinessId, backendCreative.bindingId);
        if (cancelled || !nextCreative) return;

        syncCreativePreview(nextCreative);

        if (nextCreative.status === 'ready') {
          setIsPollingCreative(false);
          await finalizeGeneratedCreative(nextCreative);
          return;
        }

        if (['failed', 'rejected'].includes(nextCreative.status)) {
          setIsPollingCreative(false);
          setGenerateError(nextCreative.errorDetail || 'Creative generation failed.');
          return;
        }

        pollTimerRef.current = setTimeout(pollCreative, 2500);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to poll workspace creative', error);
        setIsPollingCreative(false);
        setGenerateError(error?.message || 'Could not refresh creative generation status.');
      }
    };

    pollCreative();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [
    activeBusinessId,
    backendCreative?.bindingId,
    deal.getWorkspaceCreativeForBusiness,
    finalizeGeneratedCreative,
    isPollingCreative,
    phase,
    syncCreativePreview,
  ]);

  useEffect(() => {
    if (enrichmentStatus === 'ready' && generateError === enrichmentMessage) {
      setGenerateError(null);
    }
  }, [enrichmentMessage, enrichmentStatus, generateError]);

  // ── Render ──────────────────────────────────────────────────────────────

  // Phase 0: Scope selection — "What type of creative?"
  if (phase === 'scope') {
    return (
      <>
        <DealHeader
          businessName={businessName}
          currentStage="pitch"
          onBack={() => navigate(-1)}
        />

        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
          <div className="mb-8 max-w-3xl">
            <Badge variant="outline" className="rounded-full border-slate-200 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-600">
              Ad scope
            </Badge>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              What type of ad are you building?
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
              Choose how specific this pitch should be for {businessName || 'this business'}. Start with the clearest level of specificity — you can refine visuals and messaging on the next screen.
            </p>
          </div>

          <Card className="max-w-4xl border-slate-200 shadow-sm">
            <CardContent className="p-5 sm:p-6">

              <div className="grid gap-3 md:grid-cols-3">
                {SCOPE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = pendingScope === opt.id;
                  const detail = SCOPE_DETAILS[opt.id];

                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPendingScope(opt.id)}
                      className={`group h-full rounded-3xl border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-coral bg-coral/[0.035] shadow-[0_10px_28px_rgba(255,107,107,0.10)]'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/70'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-2xl border ${
                          isSelected
                            ? 'border-coral/20 bg-coral/[0.08] text-coral'
                            : 'border-slate-200 bg-slate-50 text-slate-500'
                        }`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        {isSelected && (
                          <Badge className="rounded-full border-0 bg-slate-950 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-white">
                            Selected
                          </Badge>
                        )}
                      </div>

                      <h3 className="mt-5 text-base font-semibold text-slate-950">{opt.label}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{opt.desc}</p>

                      <div className="mt-5 border-t border-slate-100 pt-4">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Best fit</p>
                        <p className="mt-1 text-sm leading-6 text-slate-700">{detail.fit}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                className="mt-6 h-12 w-full rounded-xl bg-coral text-base font-semibold text-white shadow-sm hover:bg-coral/90 sm:w-auto sm:min-w-[11rem]"
                onClick={handleScopeConfirm}
              >
                Continue
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Phase 1: Full ConfigureStep
  if (phase === 'configure') {
    if (isLoadingSourceMedia && !hasSeededApiMedia && !sourceBusinessData) {
      return (
        <>
          <DealHeader
            businessName={businessName}
            currentStage="pitch"
            onBack={() => navigate(-1)}
          />
          <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="flex flex-col items-start gap-4 p-6 sm:p-8">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-coral/[0.08] text-coral">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Loading business media</p>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-950">Pulling photos and brand inputs from the API</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
                    CornerCast is loading the current business imagery, reviews, and brand context before you generate the pitch.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      );
    }

    return (
      <>
        <DealHeader
          businessName={businessName}
          currentStage="pitch"
          onBack={() => navigate(-1)}
        />
        <ConfigureStep
          realBusinessData={realBusinessData}
          currentOrg={null}
          // Video format
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          duration={duration}
          setDuration={setDuration}
          scriptMode={scriptMode}
          setScriptMode={setScriptMode}
          silentVideo={silentVideo}
          setSilentVideo={setSilentVideo}
          // Campaign brief
          campaignBrief={campaignBrief}
          setCampaignBrief={setCampaignBrief}
          campaignOptions={campaignOptions}
          campaignOptionsError={campaignOptionsError}
          // Photos
          photos={photos}
          isUploadingPhoto={isUploadingPhoto}
          onPhotoToggle={handlePhotoToggle}
          onPhotoUpload={handlePhotoUpload}
          onPhotoDelete={handlePhotoDelete}
          // Reviews
          reviews={reviews}
          selectedReviews={selectedReviews}
          onReviewToggle={handleReviewToggle}
          // Characters
          characters={characters}
          onCharacterUpload={handleCharacterUpload}
          onCharacterUpdate={handleCharacterUpdate}
          onCharacterDelete={handleCharacterDelete}
          // Logo
          logoDataUrl={logoDataUrl}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
          logoOverlayEnabled={logoOverlayEnabled}
          setLogoOverlayEnabled={setLogoOverlayEnabled}
          logoOverlayPosition={logoOverlayPosition}
          setLogoOverlayPosition={setLogoOverlayPosition}
          logoOverlayOpacity={logoOverlayOpacity}
          setLogoOverlayOpacity={setLogoOverlayOpacity}
          // Error & actions
          onBack={() => navigate(-1)}
          onGenerate={handleGenerate}
          // Focused mode for deal flow
          focusedMode
          allowedAspectRatios={['16:9']}
          allowedDurations={[10, 15, 30]}
          hideReviews
          generateButtonLabel="Generate Pitch"
          hideBackButton
          apiError={generateError || sourceMediaError}
          generateDisabledOverride={enrichmentBlocked}
          generateDisabledTooltip={enrichmentMessage}
        />
        {enrichmentBlocked ? (
          <div className="mx-auto mt-4 max-w-5xl px-4 sm:px-6">
            <Card className={`gap-0 shadow-sm ${
              enrichmentStatus === 'failed'
                ? 'border-amber-200 bg-amber-50/70'
                : 'border-blue-200 bg-blue-50/70'
            }`}
            >
              <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                    enrichmentStatus === 'failed'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                  >
                    {enrichmentStatus === 'failed' ? (
                      <TriangleAlert className="h-5 w-5" />
                    ) : (
                      <RefreshCw className="h-5 w-5 animate-spin" />
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                      Business profile
                    </p>
                    <h2 className="mt-1 text-base font-semibold text-slate-950">
                      {enrichmentStatus === 'failed'
                        ? 'Creative generation is blocked'
                        : 'Business profile is still building'}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-slate-600">{enrichmentMessage}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    onClick={() => deal.refreshBusinessProfileForBusiness(activeBusinessId, { setActive: true })}
                    className="rounded-xl bg-white"
                  >
                    Refresh status
                  </Button>
                  {enrichmentStatus === 'failed' ? (
                    <Button onClick={handleRetryBusinessProfileEnrichment} className="rounded-xl bg-coral text-white hover:bg-coral/90">
                      Retry profile build
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </>
    );
  }

  // Phase 2: CinematicLoader generation animation
  if (phase === 'generating') {
    const generationPresentation = getGenerationPresentation(backendCreative);
    const generationFailed = ['failed', 'rejected'].includes(backendCreative?.status) || Boolean(generateError);

    return (
      <>
        <DealHeader
          businessName={businessName}
          currentStage="pitch"
        />
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div className="text-center mb-2">
            <h1 className="text-2xl font-bold text-foreground">{generationPresentation.title}</h1>
            <p className="text-muted-foreground mt-1">
              {generationPresentation.description}
            </p>
          </div>

          {generationFailed ? (
            <Card className="border-red-200 bg-red-50 shadow-sm">
              <CardContent className="space-y-4 p-5">
                <div className="flex items-start gap-3">
                  <TriangleAlert className="mt-0.5 h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm font-semibold text-red-900">Backend generation failed</p>
                    <p className="mt-1 text-sm leading-6 text-red-800">
                      {generateError || backendCreative?.errorDetail || 'The creative could not be generated.'}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-red-800">
                      You can resume this same creative from its saved backend project, or go back and change the setup before starting a new run.
                    </p>
                    {backendCreative?.bindingId ? (
                      <p className="mt-2 text-xs text-red-700">
                        Creative binding: {backendCreative.bindingId}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={handleResumeFailedCreative}
                    className="bg-coral text-white hover:bg-coral/90"
                    disabled={isSubmittingGeneration}
                  >
                    Resume generation
                  </Button>
                  <Button variant="outline" onClick={handleEditSettings} className="bg-white">
                    Edit settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-coral/20 overflow-hidden pt-0">
                <CardContent className="p-0">
                  <CinematicLoader />
                </CardContent>
              </Card>

              <div className="space-y-2 px-2">
                {GENERATION_STEPS.map((step, i) => {
                  const isComplete = i < generationPresentation.stepIndex;
                  const isActive = i === generationPresentation.stepIndex && (isSubmittingGeneration || isPollingCreative || backendCreative);

                  return (
                    <div key={step.label} className="flex items-center gap-2 text-sm">
                      {isComplete ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                      ) : isActive ? (
                        <div className="h-4 w-4 rounded-full border-2 border-coral animate-pulse" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-border" />
                      )}
                      <span className={isComplete || isActive ? 'text-foreground' : 'text-muted-foreground'}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                {savedToLibrary
                  ? "We'll save this ad to your library as soon as it's ready."
                  : "We're building a fresh ad for this business."}
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  // Phase 3: Review — video preview + share + continue
  return (
    <>
      <DealHeader
        businessName={businessName}
        currentStage="pitch"
        onBack={() => setPhase('configure')}
      />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge variant="outline" className="rounded-full border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-emerald-700">
              Pitch ready
            </Badge>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Review the creative pitch
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
              Your sample ad for {businessName || 'the business'} is ready and saved to your library for this business.
            </p>
          </div>
          <Badge variant="outline" className="w-fit rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
            {savedToLibrary ? 'Saved to library' : 'Saving to library'}
          </Badge>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.45fr_0.95fr]">
          <Card className="overflow-hidden border-slate-200 pt-0 shadow-sm">
            <CardContent className="p-0">
              <div className="relative aspect-video bg-slate-950">
                {playablePreviewVideoUrl ? (
                  <video
                    src={playablePreviewVideoUrl}
                    poster={previewPosterUrl || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                ) : previewPosterUrl && !previewPosterFailed ? (
                  <img
                    src={previewPosterUrl}
                    alt="Spec ad preview"
                    className="h-full w-full object-cover opacity-90"
                    onError={() => setPreviewPosterFailed(true)}
                  />
                ) : (
                  <CreativePreviewFallback
                    label={backendCreative?.status === 'processing' ? 'Preview rendering' : 'Preview unavailable'}
                  />
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-950/10 to-transparent" />
                <Badge className="absolute left-4 top-4 border-0 bg-black/55 text-white">
                  {duration}s {formatLabel}
                </Badge>
                {isLoadingPreviewVideo ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 backdrop-blur-[1px]">
                    <div className="flex items-center gap-2 rounded-full bg-white/92 px-4 py-2 text-sm font-medium text-slate-950 shadow-sm">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading video preview
                    </div>
                  </div>
                ) : null}
                {!playablePreviewVideoUrl ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-sm">
                      <Play className="ml-0.5 h-7 w-7 text-slate-950" />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-5 p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Creative summary</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-950">{previewAsset?.label || `${businessName || 'Business'} preview`}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Built for {businessName || 'this business'} and ready to support the commercial offer.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                    Added to library
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                    {purposeLabel}
                  </Badge>
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                    Tone: {previewCreative?.tone && previewCreative.tone !== 'auto' ? previewCreative.tone : toneLabel}
                  </Badge>
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                    {previewCreative?.aspectRatio || aspectRatio}
                  </Badge>
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                    {previewCreative?.duration || duration}s
                  </Badge>
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                    {previewCreative?.scope ? (SCOPE_OPTIONS.find((option) => option.id === previewCreative.scope)?.label || previewCreative.scope) : scopeLabel}
                  </Badge>
                </div>

                <Separator />

                {generateError ? (
                  <>
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      {generateError}
                    </div>
                    <Separator />
                  </>
                ) : null}

                <div className="space-y-3">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                    Like this one? Share it. Want a different take? Try again.
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button variant="outline" onClick={handleShare} className="h-11 flex-1 rounded-xl bg-white">
                      <Share2 className="h-4 w-4" />
                      Copy preview link
                    </Button>
                    <Button variant="outline" onClick={handleGenerateAlternative} className="h-11 flex-1 rounded-xl bg-white">
                      <RefreshCw className="h-4 w-4" />
                      Generate alternative
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Recommended next step</p>
                <h2 className="mt-3 text-2xl font-semibold leading-tight text-slate-950">Turn this pitch into an offer</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  The creative is already attached to this business. Use it to shape the commercial offer and move into proposal sending.
                </p>

                <div className="mt-5 space-y-3">
                  <Button onClick={handleBuildOffer} className="h-11 w-full rounded-xl bg-coral text-white hover:bg-coral/90">
                    Build offer
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" onClick={handleBackToBusiness} className="h-11 w-full rounded-xl bg-white">
                    <ArrowLeft className="h-4 w-4" />
                    Back to workspace
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">What was saved</p>
                <div className="mt-4 space-y-3">
                  <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <p className="text-sm text-slate-500">Creative format</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{formatLabel} · {aspectRatio}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Duration</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{duration}s</p>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <p className="text-sm text-slate-500">Objective</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{purposeLabel}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Tone</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{toneLabel}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-sm text-slate-500">Library status</p>
                    <div className="mt-2 flex items-start gap-3">
                      <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      <p className="text-sm leading-6 text-slate-600">
                        This pitch is now available in the business creative library and selected for the next offer and proposal steps.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
